import React, { useEffect, useState } from 'react';
import { useScheduleStore } from '@/stores/agents/schedule-store';
import { Calendar, Clock, CheckCircle, AlertCircle, Trash2, Check } from 'lucide-react';

const confidenceColors: Record<string, string> = {
  high: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  low: 'bg-muted text-muted-foreground',
};

const statusColors: Record<string, string> = {
  available: 'text-green-500 dark:text-green-400',
  busy: 'text-red-500 dark:text-red-400',
  sleeping: 'text-indigo-500 dark:text-indigo-400',
  social: 'text-purple-500 dark:text-purple-400',
  focused: 'text-orange-500 dark:text-orange-400',
  dnd: 'text-red-600 dark:text-red-400',
};

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function ScheduleTab() {
  const { entries, currentAvailability, isLoading, error, fetchEntries, fetchAvailability, confirmPattern, deleteEntry } = useScheduleStore();
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchEntries();
    fetchAvailability();
  }, [fetchEntries, fetchAvailability]);

  if (isLoading && entries.length === 0) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground">Loading schedule...</div>;
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Schedule</h2>
          <p className="text-sm text-muted-foreground">Your availability patterns and scheduled events</p>
        </div>
        {currentAvailability && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/50 border border-border">
            <Clock className={`w-4 h-4 ${statusColors[currentAvailability.status] || 'text-muted-foreground'}`} />
            <span className={`text-sm font-medium capitalize ${statusColors[currentAvailability.status] || 'text-foreground'}`}>{currentAvailability.status}</span>
            {currentAvailability.context && <span className="text-xs text-muted-foreground">({currentAvailability.context})</span>}
          </div>
        )}
      </div>

      {error && <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">{error}</div>}

      {entries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Calendar className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-sm">No schedule entries yet. Your agent will learn your patterns over time.</p>
          <p className="text-xs text-muted-foreground/70 mt-1">You can also add entries manually or tell your agent about your schedule.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <div key={entry.entry_id} className="p-3 bg-card border border-border rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-full">
                    {entry.entry_type === 'learned-pattern'
                      ? <AlertCircle className={`w-4 h-4 ${statusColors[entry.availability_status] || 'text-muted-foreground'}`} />
                      : <Calendar className={`w-4 h-4 ${statusColors[entry.availability_status] || 'text-muted-foreground'}`} />}
                  </div>
                  <div>
                    <div className="font-medium text-foreground">{entry.title}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${confidenceColors[entry.confidence] || 'bg-muted text-muted-foreground'}`}>
                        {entry.confidence}
                      </span>
                      <span className="text-xs text-muted-foreground/70 capitalize">{entry.entry_type.replace('-', ' ')}</span>
                      {entry.recurrence && (
                        <span className="text-xs text-muted-foreground">
                          {entry.recurrence.days_of_week.map(d => dayNames[d]).join(', ')} {entry.recurrence.start_hour}-{entry.recurrence.end_hour}
                        </span>
                      )}
                      {entry.start_time && !entry.recurrence && (
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.start_time).toLocaleDateString()} {new Date(entry.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {entry.entry_type === 'learned-pattern' && !entry.confirmed_by_user && (
                    <button
                      onClick={() => confirmPattern(entry.entry_id)}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-primary/10 text-primary rounded hover:bg-primary/20"
                    >
                      <Check className="w-3 h-3" /> Confirm
                    </button>
                  )}
                  {entry.confirmed_by_user && (
                    <CheckCircle className="w-4 h-4 text-green-500 dark:text-green-400" aria-label="User confirmed" />
                  )}
                  {confirmingDelete === entry.entry_id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { deleteEntry(entry.entry_id); setConfirmingDelete(null); }}
                        className="text-xs text-destructive hover:text-destructive/80"
                      >
                        Delete?
                      </button>
                      <button
                        onClick={() => setConfirmingDelete(null)}
                        className="text-xs text-muted-foreground"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmingDelete(entry.entry_id)}
                      className="p-1 text-muted-foreground/70 hover:text-destructive"
                      aria-label={`Delete ${entry.title}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
